import SubscriptionItemLifeTime from 'UI/SubscriptionItemLifeTime';
import { CourseTypeEnum, SubscribeDtoTypeEnum } from 'api/generated';
import arrowDow2 from 'assets/images/arrow-down2.png';
import arrowDown from 'assets/images/arrow-down.png';
import {
  getInstallmentCapabilitiesResponsive,
  getInstallmentCommonCapabilities,
  installmentBronzeCapabilities,
  installmentGoldCapabilities,
  installmentSilverCapabilities,
} from 'constant';
import { useLottieJsonWithStatus } from 'hooks/useLottieJson';
import Lottie from 'lottie-react';
import React, { FC, useEffect, useState } from 'react';
import { selectors, useAppSelector } from 'store';
import styled, { css } from 'styled-components';
import { getReducedPrice } from 'utils';

import bogoAnimationSilver from '../../../../../assets/json/bogo/TNA_AllStar.json';
import bogoAnimationSilverDark from '../../../../../assets/json/bogo/TNA_AllStar_darkmode.json';
// BOGO tier animations for bundle courses
import bogoAnimationGold from '../../../../../assets/json/bogo/TNA_MVP.json';
import bogoAnimationGoldDark from '../../../../../assets/json/bogo/TNA_MVP_darkmode.json';
import bogoAnimationBronze from '../../../../../assets/json/bogo/TNA_Rookie.json';
import bogoAnimationBronzeDark from '../../../../../assets/json/bogo/TNA_Rookie_darkmode.json';
import { PaymentOptionsSelector, TrialText } from './components';
import { BonusSection } from './components/BonusSection/BonusSection';
import {
  bronzeAnimatedMapping,
  bronzeDarkAnimatedMapping,
  goldAnimatedMapping,
  goldDarkAnimatedMapping,
  silverAnimatedMapping,
  silverDarkAnimatedMapping,
} from './constants';
import { ActivePlan, PlanLifeTime, ProductsProps } from './types';

const LifetimeProducts: FC<ProductsProps> = ({
  installmentPrice,
  purchasePrice,
  currentPayment,
  mainColor,
  directionId,
  nextPayment,
  setIsShowBonusModal,
  courseId,
  isTrialStarting,
  toggleSubscription,
  startUnsubscribeProcess,
  cancelSubscriptionDowngrade,
  startTrial,
  mostEffectiveLabel,
  showBothPrices,
  isBundle,
}) => {
  const isTrialText = currentPayment?.level === 'none' || nextPayment?.type === 'Free';
  const [activeIndex, setActiveIndex] = useState(0);
  const [activePeriod, setActivePeriod] = useState('gold');
  const [planType, setPlanType] = useState<ActivePlan>();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isDarkMode = false;
  const courses = useAppSelector(selectors.courses.selectCourses);
  const selectedCourse = useAppSelector(selectors.courses.selectCourse);

  // Get course information to determine animation overrides
  const currentCourse = courses?.find((course) => course.id === courseId) || selectedCourse;
  const courseName = currentCourse?.name || '';
  const courseType = currentCourse?.type as string;

  const courseAny = currentCourse as any;

  const normalizeStr = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

  const goldUrl = normalizeStr(courseAny?.pricingAnimationGoldUrl);
  const silverUrl = normalizeStr(courseAny?.pricingAnimationSilverUrl);
  const bronzeUrl = normalizeStr(courseAny?.pricingAnimationBronzeUrl);
  const pointerUrl = normalizeStr(courseAny?.pricingAnimationPointerUrl);

  const { data: goldUrlData, status: goldUrlStatus } = useLottieJsonWithStatus(goldUrl);
  const { data: silverUrlData, status: silverUrlStatus } = useLottieJsonWithStatus(silverUrl);
  const { data: bronzeUrlData, status: bronzeUrlStatus } = useLottieJsonWithStatus(bronzeUrl);
  const { data: pointerUrlData, status: pointerUrlStatus } = useLottieJsonWithStatus(pointerUrl);

  const getPlanAnimationKey = (planType: 'gold' | 'silver' | 'bronze') => {
    const url = planType === 'gold' ? goldUrl : planType === 'silver' ? silverUrl : bronzeUrl;
    const loaded =
      planType === 'gold'
        ? Boolean(goldUrlData)
        : planType === 'silver'
        ? Boolean(silverUrlData)
        : Boolean(bronzeUrlData);

    if (url) return loaded ? `pricing:${planType}:url:${url}:ready` : `pricing:${planType}:url:${url}:pending`;

    const rawOverrideKey =
      planType === 'gold'
        ? courseAny?.pricingAnimationGoldKey
        : planType === 'silver'
        ? courseAny?.pricingAnimationSilverKey
        : courseAny?.pricingAnimationBronzeKey;

    const overrideKey = normalizeStr(rawOverrideKey) || 'auto';
    return `pricing:${planType}:key:${overrideKey}:direction:${directionId}`;
  };

  const pointerLottieKey = pointerUrl
    ? `pricing:pointer:url:${pointerUrl}:${pointerUrlStatus}`
    : 'pricing:pointer:default';

  // Function to determine if this is a bundle course
  const isBundleCourse = () => {
    // Use the isBundle prop passed from parent as primary source
    if (isBundle) {
      return true;
    }

    // Backend uses: where: { type: CourseType.BUNDLE } where CourseType.BUNDLE = 'bundle'
    // Match the exact same logic here
    const result = courseType === 'bundle' || (currentCourse as any)?.type === 'bundle';
    return result;
  };

  // Function to get animation data with course-specific overrides
  const getAnimationData = (planType: string, isDark = false) => {
    const hasUrl =
      planType === 'gold'
        ? Boolean(goldUrl)
        : planType === 'silver'
        ? Boolean(silverUrl)
        : planType === 'bronze'
        ? Boolean(bronzeUrl)
        : false;

    const urlStatus =
      planType === 'gold'
        ? goldUrlStatus
        : planType === 'silver'
        ? silverUrlStatus
        : planType === 'bronze'
        ? bronzeUrlStatus
        : 'idle';

    const urlData =
      planType === 'gold'
        ? goldUrlData
        : planType === 'silver'
        ? silverUrlData
        : planType === 'bronze'
        ? bronzeUrlData
        : null;

    if (hasUrl) {
      if (urlStatus === 'loaded' && urlData) return urlData;
      if (urlStatus === 'loading') return null;
      // if error, fall through to existing fallback logic
    }

    const rawOverrideKey =
      planType === 'gold'
        ? courseAny?.pricingAnimationGoldKey
        : planType === 'silver'
        ? courseAny?.pricingAnimationSilverKey
        : planType === 'bronze'
        ? courseAny?.pricingAnimationBronzeKey
        : undefined;
    const overrideKey = typeof rawOverrideKey === 'string' ? rawOverrideKey.trim() : '';

    if (overrideKey && overrideKey !== 'auto') {
      switch (overrideKey) {
        case 'bogo': {
          switch (planType) {
            case 'gold':
              return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
            case 'silver':
              return isDark ? bogoAnimationSilverDark : bogoAnimationSilver;
            case 'bronze':
              return isDark ? bogoAnimationBronzeDark : bogoAnimationBronze;
            default:
              return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
          }
        }
        case 'direction': {
          switch (planType) {
            case 'gold':
              return isDark
                ? goldDarkAnimatedMapping[directionId as keyof typeof goldDarkAnimatedMapping]
                : goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping];
            case 'silver':
              return isDark
                ? silverDarkAnimatedMapping[directionId as keyof typeof silverDarkAnimatedMapping]
                : silverAnimatedMapping[directionId as keyof typeof silverAnimatedMapping];
            case 'bronze':
              return isDark
                ? bronzeDarkAnimatedMapping[directionId as keyof typeof bronzeDarkAnimatedMapping]
                : bronzeAnimatedMapping[directionId as keyof typeof bronzeAnimatedMapping];
            default:
              return null;
          }
        }
        default:
          break;
      }
    }

    const isBundleResult = isBundleCourse();

    // PRIORITY 1: Bundle courses always use BOGO animations
    if (isBundleResult) {
      switch (planType) {
        case 'gold':
          return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
        case 'silver':
          return isDark ? bogoAnimationSilverDark : bogoAnimationSilver;
        case 'bronze':
          return isDark ? bogoAnimationBronzeDark : bogoAnimationBronze;
        default:
          return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
      }
    }

    // Default behavior: use direction-based animations
    switch (planType) {
      case 'gold':
        return isDark
          ? goldDarkAnimatedMapping[directionId as keyof typeof goldDarkAnimatedMapping]
          : goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping];
      case 'silver':
        return isDark
          ? silverDarkAnimatedMapping[directionId as keyof typeof silverDarkAnimatedMapping]
          : silverAnimatedMapping[directionId as keyof typeof silverAnimatedMapping];
      case 'bronze':
        return isDark
          ? bronzeDarkAnimatedMapping[directionId as keyof typeof bronzeDarkAnimatedMapping]
          : bronzeAnimatedMapping[directionId as keyof typeof bronzeAnimatedMapping];
      default:
        return null;
    }
  };

  // Function to get color scheme with course-specific overrides
  const getMainColor = () => {
    const override = typeof courseAny?.upsellPrimaryColor === 'string' ? courseAny.upsellPrimaryColor.trim() : '';
    if (override) return override;

    const isBundleResult = isBundleCourse();

    // PRIORITY 1: Bundle courses always use gradient theme
    if (isBundleResult) {
      return 'linear-gradient(to right, #ED5A26, #1CBF73)'; // Gradient for bundles (matches bundleColors.primary)
    }

    // Default behavior: use original mainColor
    return mainColor;
  };

  const isResponsive = screenWidth <= 1180;

  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Create plans (do not memoize) so URL-loaded JSON can replace fallbacks once it arrives
  const plansLifeTime: Array<PlanLifeTime> = [
    {
      installmentPrice: installmentPrice?.gold,
      purchasePrice: purchasePrice?.gold,
      type: SubscribeDtoTypeEnum.Gold,
      capabilities: installmentGoldCapabilities,
      capabilitiesResponsive: getInstallmentCapabilitiesResponsive('gold', courseName, isBundleCourse()),
      animationData: getAnimationData('gold', false),
      darkAnimationData: getAnimationData('gold', true),
      animationKey: getPlanAnimationKey('gold'),
    },
    {
      installmentPrice: null, // Disabled - show N/A instead
      purchasePrice: purchasePrice?.silver,
      type: SubscribeDtoTypeEnum.Silver,
      capabilities: installmentSilverCapabilities,
      capabilitiesResponsive: getInstallmentCapabilitiesResponsive('silver', courseName, isBundleCourse()),
      animationData: getAnimationData('silver', false),
      darkAnimationData: getAnimationData('silver', true),
      animationKey: getPlanAnimationKey('silver'),
    },
    {
      installmentPrice: null, // Disabled - show N/A instead
      purchasePrice: purchasePrice?.bronze,
      type: SubscribeDtoTypeEnum.Bronze,
      capabilities: installmentBronzeCapabilities,
      capabilitiesResponsive: getInstallmentCapabilitiesResponsive('bronze', courseName, isBundleCourse()),
      animationData: getAnimationData('bronze', false),
      darkAnimationData: getAnimationData('bronze', true),
      animationKey: getPlanAnimationKey('bronze'),
    },
  ];

  if (!directionId) return <></>;

  const calculateCurrentPrice = (
    amount: number | undefined,
    defaultCouponDiscount?: number,
    defaultCouponDiscountType?: string,
  ) => {
    if (!amount) return;

    if (defaultCouponDiscount && amount - defaultCouponDiscount >= 1 && defaultCouponDiscountType === 'amount') {
      return getReducedPrice(amount - defaultCouponDiscount);
    }

    if (defaultCouponDiscount && defaultCouponDiscountType === 'percentage') {
      const discountAmount = amount * (defaultCouponDiscount / 100);

      if (amount - discountAmount >= 1) {
        return getReducedPrice(amount - discountAmount);
      }
    }

    return getReducedPrice(amount);
  };

  // Filter out Bronze for new users on certification courses
  const isCertificate = courseType === 'certificate' || courseType === CourseTypeEnum.Certificate;
  const isNewUserForBronze =
    (currentPayment?.level as string) !== 'bronze' && (currentPayment?.level as any) !== SubscribeDtoTypeEnum.Bronze;
  const plansToRender = plansLifeTime.filter((plan) => {
    if (isCertificate && isNewUserForBronze && plan.type === SubscribeDtoTypeEnum.Bronze) {
      return false;
    }
    return true;
  });

  console.log('🔍 LifetimeProducts Debug:', {
    isBundle,
    showBothPrices,
    plansLifeTimeLength: plansLifeTime.length,
    plansToRenderLength: plansToRender.length,
    installmentPriceStructure: installmentPrice,
    purchasePriceStructure: purchasePrice,
    plansToRenderDetails: plansToRender.map((plan) => ({
      type: plan.type,
      hasInstallment: !!plan.installmentPrice,
      hasPurchase: !!plan.purchasePrice,
      installmentAmount: plan.installmentPrice?.amount,
      purchaseAmount: plan.purchasePrice?.amount,
    })),
  });

  return (
    <Root style={{ marginTop: isResponsive ? '-557px' : '15px' }}>
      <Wrapper>
        <Container $isShowPurchase={false} isResponsive={isResponsive}>
          <ImageDivArrowRight>
            {pointerUrl ? (
              pointerUrlStatus === 'loaded' && pointerUrlData ? (
                <Lottie
                  key={pointerLottieKey}
                  animationData={pointerUrlData}
                  style={{ width: '70px', height: '70px' }}
                />
              ) : pointerUrlStatus === 'error' ? (
                <img src={arrowDow2} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
              ) : (
                <div style={{ width: '70px', height: '70px' }} />
              )
            ) : (
              <img src={arrowDow2} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
            )}
          </ImageDivArrowRight>

          <ImageDivArrowLeft>
            {pointerUrl ? (
              pointerUrlStatus === 'loaded' && pointerUrlData ? (
                <Lottie
                  key={pointerLottieKey}
                  animationData={pointerUrlData}
                  style={{ width: '70px', height: '70px', transform: 'scaleX(-1)' }}
                />
              ) : pointerUrlStatus === 'error' ? (
                <img src={arrowDown} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
              ) : (
                <div style={{ width: '70px', height: '70px' }} />
              )
            ) : (
              <img src={arrowDown} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
            )}
          </ImageDivArrowLeft>

          {currentPayment?.level === 'none' && (
            <BonusSection
              isProduct={true}
              isCertificate={true}
              setIsShowBonusModal={setIsShowBonusModal}
              course={{
                ...currentCourse,
                type: isBundleCourse() ? 'bundle' : currentCourse?.type,
              }}
            />
          )}

          {plansLifeTime && (
            <PlansContainer>
              {!isResponsive && (!showBothPrices || isBundle) && (
                <PaymentOptionsSelector
                  mainColor={getMainColor()}
                  goldPurchasePrice={calculateCurrentPrice(
                    purchasePrice?.gold?.amount,
                    purchasePrice?.gold?.defaultCoupon?.discountAmount,
                    purchasePrice?.gold?.defaultCoupon?.discountType,
                  )}
                  goldInstallmentPrice={calculateCurrentPrice(
                    installmentPrice?.gold?.amount,
                    installmentPrice?.gold?.defaultCoupon?.discountAmount,
                    installmentPrice?.gold?.defaultCoupon?.discountType,
                  )}
                  subscribeType={SubscribeDtoTypeEnum.Gold}
                  isBundle={isBundle}
                  courseType={courseType}
                  indexPlan={0}
                />
              )}
              {!isResponsive &&
                getInstallmentCommonCapabilities(courseName, isBundleCourse()).map((cap, index) => (
                  <div style={{ alignItems: 'center', width: '230px' }} key={index}>
                    <CapabilitiesContainer>{cap.title}</CapabilitiesContainer>
                  </div>
                ))}
            </PlansContainer>
          )}

          {plansToRender.map((plan, index) => (
            <SubscriptionItemLifeTime
              key={index}
              isResponsive={isResponsive}
              // @ts-ignore
              plan={plan}
              termsCount={1}
              setIsShowBonusModal={setIsShowBonusModal}
              indexPlan={index}
              animationData={isDarkMode ? plan?.darkAnimationData : plan?.animationData}
              animationKey={plan?.animationKey}
              setPlanType={setPlanType}
              planType={planType}
              mainColor={getMainColor()}
              isShow={activeIndex === index}
              activePeriod={activePeriod}
              setActivePeriod={setActivePeriod}
              setActiveIndex={setActiveIndex}
              cancelSubscriptionDowngrade={cancelSubscriptionDowngrade}
              toggleSubscription={toggleSubscription}
              courseId={courseId}
              isCurrentSubscription={currentPayment?.level === plan?.type}
              currentPayment={currentPayment}
              goldPurchasePrice={calculateCurrentPrice(
                purchasePrice?.gold?.amount,
                purchasePrice?.gold?.defaultCoupon?.discountAmount,
                purchasePrice?.gold?.defaultCoupon?.discountType,
              )}
              goldInstallmentPrice={calculateCurrentPrice(
                installmentPrice?.gold?.amount,
                installmentPrice?.gold?.defaultCoupon?.discountAmount,
                installmentPrice?.gold?.defaultCoupon?.discountType,
              )}
              showBothPrices={showBothPrices}
              mostEffectiveLabel={mostEffectiveLabel}
              isBundle={isBundle}
            />
          ))}
        </Container>
        {isTrialText && (
          <TrialText
            isDowngrade={nextPayment?.type === 'Free'}
            cancelSubscriptionDowngrade={() => cancelSubscriptionDowngrade(courseId, true)}
            currentPayment={currentPayment}
            startTrial={() => startTrial(courseId)}
            isTrialStarting={isTrialStarting}
            startUnsubscribeProcess={startUnsubscribeProcess}
          />
        )}
      </Wrapper>
    </Root>
  );
};

export default LifetimeProducts;

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;

  grid-gap: 32px;

  display: flex;
  flex-direction: column;
  justify-content: start;
`;

const PlansContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 522px;
`;

const ImageDivArrowRight = styled.div`
  width: 70px;
  height: 70px;
  position: absolute;
  top: 40px;
  right: 75px;
  z-index: 1;

  @media (max-width: 1180px) {
    top: 190px;
    right: 30px;
  }
`;

const ImageDivArrowLeft = styled.div`
  width: 70px;
  height: 70px;
  position: absolute;
  top: 95px;
  left: 185px;
  z-index: 1;

  @media (max-width: 1180px) {
    top: 140px;
    left: 90px;
  }

  @media (max-width: 570px) {
    top: 130px;
    left: 30px;
  }
`;

const CapabilitiesContainer = styled.div`
  ${({ theme: { typography } }) => typography.body_large_semibold_16}
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
  margin-bottom: 21px;
`;

const Container = styled.div<{ $isShowPurchase: boolean; isResponsive: boolean }>`
  display: ${({ isResponsive }) => (isResponsive ? 'unset' : 'flex')};
  height: max-content;
  gap: 20px;

  ${({ $isShowPurchase }) =>
    $isShowPurchase
      ? css`
          margin: 0 auto auto;
          display: grid;
          grid-template-columns: 352px;
        `
      : css``}

  @media (max-width: 1180px) {
    width: 500px;
  }

  @media (max-width: 570px) {
    width: 370px;
  }

  @media (max-width: 370px) {
    width: 320px;
  }
`;
